import { Dialog } from '@ariakit/react';
import type { DialogProps } from '@ariakit/react';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CSSProperties, FC, PropsWithChildren, ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { ActionModeProvider } from 'src/components/action-mode';
import { ActionGroupModeProvider } from 'src/components/buttons-and-actions/action-group';
import { Button } from 'src/components/buttons-and-actions/button';
import { XIcon } from 'src/components/media-and-icons/icons';
import { Heading } from 'src/components/text/heading';
import { ModalContextProvider } from 'src/hooks/use-modal';
import { sonnerContainerId } from 'src/hooks/use-sonner';
import { useStorybook } from 'src/hooks/use-storybook';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

type Size = 'default' | 'sm' | 'md' | 'lg';

const sizeWidths: Record<Size, string> = {
  default: '600px',
  sm: '300px',
  md: '450px',
  lg: '900px',
};

type Props = {
  icon?: IconDefinition | undefined;
  title: string;
  size?: Size;
  /**
   * Be very careful with this one. Usually there is no good reason why you should
   * NOT be able to close a modal. At the end you cannot force people to do something
   * within the modal or keep it open for some time. They could simply refresh or navigate
   * away from the page.
   */
  hideClose?: boolean;
  /**
   * For non-form modals: primary action button.
   * When a Form is inside the Modal, use Form's submitButton instead.
   */
  confirmButton: ReactNode | null;
  /**
   * By default, a "Close" button is shown. Set to null to hide. Set to an object to customize.
   * When a Form is inside the Modal, use Form's cancelOrResetButton instead.
   */
  closeButton: ReactNode | null;
  onDismiss?: () => void;
} & StrictOmit<DialogProps, 'open' | 'backdrop' | 'onClose' | 'style'>;

export const Modal: FC<Props> = ({
  icon,
  title,
  size = 'default',
  hideClose,
  confirmButton,
  closeButton,
  onDismiss,
  children,
  ...dialogProps
}) => {
  const isNewDesignActive = useIsNewDesignActive();
  const { isStorybook } = useStorybook();

  const contextValue = useMemo(
    () => ({
      close: onDismiss,
    }),
    [onDismiss]
  );

  useEffect(() => {
    // Layout fix for ariakit dialog
    // see https://github.com/ariakit/ariakit/issues/4345#issuecomment-2579469808
    document.documentElement.classList.add(
      'overflow-x-visible!',
      '[&_body]:pr-0!'
    );
    return () => {
      document.documentElement.classList.remove(
        'overflow-x-visible!',
        '[&_body]:pr-0!'
      );
    };
  }, []);

  const shouldRenderFooter = Boolean(confirmButton) || Boolean(closeButton);

  return (
    <ActionModeProvider value={null}>
      <ActionGroupModeProvider value="single">
        <Dialog
          {...dialogProps}
          open
          getPersistentElements={() =>
            [document.getElementById(sonnerContainerId)].filter(
              (element) => element !== null
            )
          }
          backdrop={
            <div
              className={cn(
                `
                  bg-background-surface-inverted-default/54
                  legacy:bg-[#00000054]
                `,
                !isStorybook && `animate-fade-in-fast`
              )}
            />
          }
          onClose={onDismiss}
          style={
            {
              '--modal-size': sizeWidths[size],
              '--modal-content-padding': 'var(--spacing-xl)',
            } as CSSProperties
          }
          className={cn(
            `
              fixed inset-x-0 top-[10vh] mx-auto w-(--modal-size)
              max-w-[calc(100%-var(--spacing-xl)*2)] rounded-sm
              bg-background-surface-default shadow-elevation-md
            `,
            `
              legacy:max-w-[calc(100%-1.5rem*2)] legacy:rounded-[0.6rem]
              legacy:bg-white legacy:shadow-[0_0.5rem_1.5rem_rgba(0,0,0,0.5)]
            `,
            `flex max-h-[80svh] flex-col overflow-hidden`,
            !isStorybook && `animate-fade-in-fast`
          )}
        >
          <ModalContextProvider value={contextValue}>
            {title && (
              <div
                className={cn(
                  `
                    grid shrink-0 grid-flow-col grid-cols-[auto_min-content]
                    items-center gap-xl rounded-t-sm
                    bg-background-surface-inverted-default px-xl py-lg
                    text-headline-md break-all
                    text-foreground-surface-inverted-default
                  `,
                  icon && `grid-cols-[auto_1fr]`,
                  `
                    legacy:gap-[1.5rem] legacy:bg-legacy-brand legacy:p-[1.5rem]
                    legacy:text-[2rem] legacy:text-white
                  `
                )}
              >
                {icon && <FontAwesomeIcon icon={icon} />}

                {isNewDesignActive ? (
                  <Heading
                    mode="title"
                    size="md"
                    className={`
                      not-legacy:text-foreground-surface-inverted-default
                    `}
                  >
                    {title}
                  </Heading>
                ) : (
                  <p
                    className={`
                      legacy:text-[2rem] legacy:leading-[2.85714rem]
                      legacy:font-[900]
                    `}
                  >
                    {title}
                  </p>
                )}

                {!hideClose && (
                  <Button
                    mode="icon-inverse"
                    onClick={onDismiss}
                    className={cn(
                      `
                        not-legacy:text-foreground-surface-inverted-subdued
                        not-legacy:hover:text-foreground-surface-inverted-hover
                        not-legacy:active:text-foreground-surface-inverted-active
                      `,
                      `
                        legacy:self-start legacy:text-[2.2rem]
                        legacy:leading-[2.53rem]
                        legacy:text-legacy-brand-light-1
                        legacy:not-disabled:not-active:hover:text-legacy-brand-light-2
                        legacy:focus:text-legacy-brand-light-1!
                      `
                    )}
                    aria-label="Close modal"
                  >
                    {isNewDesignActive ? (
                      <XIcon size="md" />
                    ) : (
                      <FontAwesomeIcon icon={faTimes} />
                    )}
                  </Button>
                )}
              </div>
            )}

            <div
              className={cn(
                `h-full min-h-0 rounded-b-sm p-(--modal-content-padding)`,
                `
                  overflow-y-auto
                  [&:has([data-slot='modal-footer'])]:pb-0
                `
              )}
            >
              {children}

              {shouldRenderFooter && (
                <ModalFooter>
                  {closeButton ? closeButton : <span />}

                  {confirmButton ? confirmButton : <span />}
                </ModalFooter>
              )}
            </div>
          </ModalContextProvider>
        </Dialog>
      </ActionGroupModeProvider>
    </ActionModeProvider>
  );
};

export const ModalFooter: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div
      data-slot="modal-footer"
      className={cn(
        `
          mt-(--modal-content-padding) grid grid-flow-col justify-between gap-xl
          rounded-b-sm bg-background-surface-default
        `,
        `
          sticky bottom-0 -mx-(--modal-content-padding) shrink-0
          p-(--modal-content-padding)
          before:pointer-events-none before:absolute before:inset-x-0
          before:-top-(--modal-content-padding) before:h-xl
          before:bg-linear-to-t before:from-background-surface-default
          before:to-transparent before:content-[''] before:select-none
          legacy:p-[1.5rem]
        `,
        `legacy:gap-[2rem] legacy:rounded-b-[0.6rem] legacy:bg-white`
      )}
    >
      {children}
    </div>
  );
};
